import Footer from "components/sections/footer"
import Header from "components/sections/headerV2"
import Seo from "components/sections/seo"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React from "react"
import Template, { ClinicTemplateDetailsProps } from "./template"
import {
  getLocalBusinessSchema,
  getSchemaWithFallback,
} from "../../utils/schemaHelpers"
import {
  musicTherapyAt,
  offersPhysicalTherapy,
  seoRewriteFor,
} from "../../utils/clinicOverrides"
import { insurerProse, paymentAccepted } from "../../utils/clinicInsurers"
import { localiseFaqs, withGeneratedSchema } from "../../utils/clinicFaqs"
import COMPONENTS from "../../utils/componentConstants"

const SITE_URL = "https://www.corticacare.com"

const TemplateContainer = ({
  data: {
    clinic,
    closingCta,
    footer,
    header,
    insuranceBanners,
    insurerLogos,
    overrideAssets,
    services,
    template,
  },
}) => {
  // Braintree, Danvers and Naperville name music therapy in their SEO title
  // and description but do not offer it: all three are outside California. The
  // page copy is filtered the same way, so the metadata has to match.
  const seoRewrite = seoRewriteFor(clinic.slug)
  const meta =
    clinic.meta && seoRewrite
      ? {
          ...clinic.meta,
          description: { description: seoRewrite.description },
          title: seoRewrite.title,
        }
      : clinic.meta

  const fallbackSchema = getLocalBusinessSchema(clinic)

  // Use Contentful json if present, otherwise use fallback
  const baseSchema = getSchemaWithFallback(clinic.meta?.json, fallbackSchema)

  // The FAQ markup is generated from the same questions the page renders, so
  // the two cannot disagree. Every centre's hand-written version had gone
  // stale, still naming three questions the pages no longer show.
  const faqSection = (clinic.sections || []).find(
    section => section.__typename === COMPONENTS.Faq
  )
  const music = musicTherapyAt(clinic.slug)
  const physical = offersPhysicalTherapy(clinic.slug)
  const schema = withGeneratedSchema(baseSchema, {
    canonicalUrl: `${SITE_URL}/locations/${clinic.slug}`,
    clinicNode: fallbackSchema,
    keepService: name =>
      (music || !/music\s+therap/i.test(name)) &&
      (physical || !/physical\s+therap/i.test(name)),
    payment: paymentAccepted(clinic.slug),
    questions: localiseFaqs(
      faqSection?.questions,
      clinic.name,
      music,
      insurerProse(clinic.slug),
      physical
    ),
  })

  // Interim: clinics have no field pointing at an insurer-logo set yet, so use
  // the first set that has logos. Swap this for a per-clinic reference once the
  // regional sets exist in Contentful.
  const insuranceLogos =
    (insuranceBanners?.nodes || []).find(
      banner => banner.providersLogos?.length
    )?.providersLogos || []

  return (
    <>
      <Seo
        {...meta}
        id={clinic.id}
        /* Seo renders `json` as a second ld+json block of its own. It is the
           raw Contentful field, so it would sit beside schemaMarkup carrying
           the stale three-question FAQ this page replaces. They were already
           duplicates of each other before; now they would contradict. */
        json={undefined}
        schemaMarkup={schema}
        slug={`${template.slugPrefix}/${clinic.slug}`}
      />
      <Header {...header} />
      <Template
        {...template}
        clinic={clinic}
        closingCta={closingCta}
        insuranceLogos={insuranceLogos}
        insurerLogos={insurerLogos?.nodes || []}
        overrideAssets={overrideAssets?.nodes || []}
        services={services}
      />
      <Footer {...footer} />
    </>
  )
}

TemplateContainer.props = {
  ...ClinicTemplateDetailsProps,
  meta: PropTypes.shape(Seo.props),
}

TemplateContainer.propTypes = {
  data: PropTypes.shape({
    clinic: PropTypes.shape(TemplateContainer.props),
    closingCta: PropTypes.object,
    footer: PropTypes.shape(Footer.props),
    header: PropTypes.shape(Header.props),
    insuranceBanners: PropTypes.shape({
      nodes: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          providersLogos: PropTypes.arrayOf(
            PropTypes.shape({
              file: PropTypes.shape({ url: PropTypes.string }),
              title: PropTypes.string,
            })
          ),
        })
      ),
    }),
    insurerLogos: PropTypes.object,
    overrideAssets: PropTypes.object,
    services: PropTypes.object,
    template: PropTypes.shape(Template.props),
  }),
}

export default TemplateContainer

export const query = graphql`
  fragment ButtonV2Fields on ContentfulComponentButtonV2 {
    id
    accessibilityLabel
    showArrow
    buttonSize
    buttonStyle
    path
    text
  }
  fragment ImageFields on ContentfulAsset {
    title
    description
    file {
      url
    }
  }
  query Clinic($slug: String) {
    header: contentfulGlobalHeader(name: { eq: "Header" }) {
      announcement {
        json
      }
      menu {
        menus {
          label
          links {
            text
            path
            nestedLinks {
              text
              path
            }
          }
        }
      }
      utilityLinks {
        path
        text
      }
      cta {
        href: path
        text
      }
      callsToAction {
        ...ButtonV2Fields
      }
    }
    footer: contentfulGlobalFooter(name: { eq: "Footer" }) {
      menu {
        menus {
          label
          links {
            path
            text
          }
        }
      }
      scheduleAppointment {
        path
        text
      }
      socialLinks {
        path
        text
      }
      utilityLinks {
        path
        text
      }
    }
    clinic: contentfulClinic(slug: { eq: $slug }) {
      detailedAddress
      fax
      id
      name
      phone
      postalCode
      slug
      videoUrl
      whyChooseContent {
        json
      }
      sections {
        __typename
        ... on ContentfulSectionSplitContent {
          __typename
          backgroundColor
          reverse
          image {
            image {
              alt: description
              title
              file {
                url
              }
            }
          }
          intro {
            eyebrow: subTitle
            action: callToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
        }
        ... on ContentfulSectionFaq {
          __typename
          action: callToAction {
            accessibilityLabel
            color
            isOutlined
            isSmall
            href: path
            text
          }
          intro {
            eyebrow: subTitle
            action: callToAction {
              accessibilityLabel
              color
              isOutlined
              isSmall
              href: path
              text
            }
            text {
              json
            }
            title {
              text {
                json
              }
            }
          }
          questions {
            title: question
            text: answer {
              json
            }
          }
        }
        ... on ContentfulSectionDoctors {
          __typename
          title
          doctors {
            department
            email
            name
            phone
            role
            slug
            details {
              json
            }
            picture {
              fluid(maxHeight: 200, maxWidth: 200, quality: 90) {
                src
              }
            }
          }
        }
      }
      address {
        lat
        lon
      }
      cta {
        buttonText
        style
        action {
          __typename
          ... on ContentfulActionRedirect {
            __typename
            openInANewTab
            url
          }
        }
      }
      doctors {
        department
        email
        name
        phone
        role
        slug
        details {
          json
        }
        picture {
          title
          fluid(maxHeight: 200, maxWidth: 200, quality: 90) {
            src
          }
        }
      }
      headline: childContentfulClinicHeadlineRichTextNode {
        json
      }
      gallery {
        title
        file {
          url
        }
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      image {
        title
        file {
          url
        }
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      sidebarDetails: childContentfulClinicSidebarDetailsRichTextNode {
        json
      }
      meta: seoMetadata {
        canonical
        keywords
        title
        type
        json
        abstract {
          abstract
        }
        description {
          description
        }
        image {
          file {
            url
          }
        }
      }
    }
    # The services line-up is the same at every centre, so clinic pages reuse
    # the shared section the homepage already renders.
    services: contentfulSectionHomeSplitStickyV2 {
      id
      ctaButton {
        ...ButtonV2Fields
      }
      cards {
        id
        title
        text {
          text
        }
        url
        image {
          ...ImageFields
          fluid(maxWidth: 962, quality: 90) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
      }
    }
    # The closing "Together, we can unlock…" call to action, shared with the
    # homepage so the two never drift apart.
    closingCta: contentfulSectionSplitSectionV2(
      title: { eq: "Home Split Section" }
    ) {
      id
      callsToAction {
        ...ButtonV2Fields
      }
      subTitle
      heading
      contentOrientation
      image {
        ...ImageFields
        fluid(maxWidth: 686, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      content {
        json
      }
    }
    # Assets referenced by the temporary per-clinic overrides in
    # utils/clinicOverrides. Keep this title list in step with that file.
    overrideAssets: allContentfulAsset(
      filter: {
        title: {
          in: [
            "Shrewsbury 1"
            "Cortica San Diego"
            "Plano 1"
            "Kids playing with toys on a green mat"
            "A child in speech language therapy at Cortica"
            "children participating in a yoga class for sensorimotor play treatment"
            "Children stretching"
            "Reading to the class of young children"
            "Applied Behavior Analysis"
            "Children with blocks"
            "Children at table with iPad"
            "Developmental Therapies"
            "Woman and child with blocks"
            "Woman with child"
            "Developmental Therapies at Cortica"
          ]
        }
      }
    ) {
      nodes {
        title
        file {
          url
        }
        fluid(maxWidth: 962, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
    }
    # Per-insurer logos, matched by title to the payer lists in
    # utils/clinicInsurers. Uploading one with the right title is all it takes
    # for it to appear on the centres that accept that insurer.
    insurerLogos: allContentfulAsset(
      filter: { title: { regex: "/^Insurer Logo - /" } }
    ) {
      nodes {
        title
        file {
          url
        }
      }
    }
    # Reusable sets of insurer logos. Used as a fallback on centres whose
    # region has not been mapped yet.
    insuranceBanners: allContentfulSectionInsuranceBanner {
      nodes {
        name
        providersLogos {
          title
          file {
            url
          }
        }
      }
    }
    template: contentfulWebPageTemplate(model: { eq: "Clinic" }) {
      slugPrefix
      title
      sections {
        __typename
        ... on ContentfulSubheader {
          __typename
          backgroundColor
        }
        ... on ContentfulTemplateSection {
          __typename
          title
          additionalSettings {
            enableDirectionsCTA
          }
        }
      }
    }
  }
`
